/**
 * useClinicImprovementActivities — GET /v1/clinics/{cid}/improvement-activities
 *
 * Read endpoint for IA attestations. New in backend v0.4.0.
 *
 * Powers the IA bulk-save modal pre-population: instead of starting from
 * an empty form every time, the AM sees the current attestations and can
 * edit-and-resave (still replace-all semantics on the PUT — this is a
 * read-then-replace UX, not partial updates).
 *
 * Convention (matches v0.3.1 meetings list):
 *   - 200 + empty array when no attestations exist
 *   - 404 only if the clinic itself doesn't exist
 */
import { useQuery } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type { IaAttestationListResponse } from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface UseClinicImprovementActivitiesOptions {
  year?: number;
  enabled?: boolean;
}

export function useClinicImprovementActivities(
  clinicId: number,
  options: UseClinicImprovementActivitiesOptions = {},
) {
  const { year, enabled } = options;
  return useQuery({
    queryKey: queryKeys.ia.byClinic(clinicId, year),
    queryFn: async (): Promise<IaAttestationListResponse> => {
      const params: Record<string, number> = {};
      if (year !== undefined) params.year = year;
      const { data } = await getApiClient().get<IaAttestationListResponse>(
        `/v1/clinics/${clinicId}/improvement-activities`,
        { params: Object.keys(params).length > 0 ? params : undefined },
      );
      return data;
    },
    staleTime: 30_000,
    enabled: enabled ?? Number.isFinite(clinicId),
  });
}
